import { PermissionResource, PermissionType, ReviewPermissionType, UserRole, } from '../../models/index.js';
import { hasPermission, hasRole } from './permission.js';
function permissionFactory(projectId, user) {
    return (permission) => hasPermission(projectId, permission, user);
}
export const createPermissions = (projectId, user) => {
    const can = permissionFactory(projectId, user);
    return {
        edit: {
            overview: can([{ permission: PermissionType.EDIT, resource: PermissionResource.OVERVIEW }]),
            budget: can([{ permission: PermissionType.EDIT, resource: PermissionResource.BUDGET }]),
            forecast: can([{ permission: PermissionType.EDIT, resource: PermissionResource.FORECAST }]),
            ata: can([{ permission: PermissionType.EDIT, resource: PermissionResource.ATA }]),
            invoice: can([{ permission: PermissionType.EDIT, resource: PermissionResource.INVOICE }]),
        },
        comment: {
            overview: can([
                { permission: PermissionType.COMMENT, resource: PermissionResource.OVERVIEW },
            ]),
            budget: can([{ permission: PermissionType.COMMENT, resource: PermissionResource.BUDGET }]),
            forecast: can([
                { permission: PermissionType.COMMENT, resource: PermissionResource.FORECAST },
            ]),
            ata: can([{ permission: PermissionType.COMMENT, resource: PermissionResource.ATA }]),
            invoice: can([{ permission: PermissionType.COMMENT, resource: PermissionResource.INVOICE }]),
        },
        view: {
            overview: can([{ permission: PermissionType.VIEW, resource: PermissionResource.OVERVIEW }]),
            budget: can([{ permission: PermissionType.VIEW, resource: PermissionResource.BUDGET }]),
            forecast: can([{ permission: PermissionType.VIEW, resource: PermissionResource.FORECAST }]),
            ata: can([{ permission: PermissionType.VIEW, resource: PermissionResource.ATA }]),
            invoice: can([{ permission: PermissionType.VIEW, resource: PermissionResource.INVOICE }]),
        },
        review: can([
            { permission: ReviewPermissionType.ALLOWED, resource: PermissionResource.REVIEW },
        ]),
    };
};
const createRolePermissions = (user) => {
    return {
        CESG: hasRole(UserRole.CESG, user),
        GUEST: hasRole(UserRole.GUEST, user),
        ADMIN: hasRole(UserRole.ADMIN, user),
    };
};
// PERMISSIONS
// Översikt
function overviewPermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        edit: {
            overview: permTo.edit.overview,
            info: permTo.edit.overview,
            comments: permTo.comment.overview && role.CESG,
            reviewers: permTo.edit.overview && role.CESG,
        },
        read: {
            info: permTo.view.overview,
            result: role.CESG,
            comments: role.CESG,
            reviewers: role.CESG,
        },
    };
}
// Kalkyl/Budget
function calculateBudgetPermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        edit: {
            budget: permTo.edit.budget,
            comments: permTo.comment.budget && role.CESG,
        },
        read: {
            comments: role.CESG,
        },
    };
}
// Forecast
function forecastPermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        edit: {
            forecast: permTo.edit.forecast,
            accountTranslation: permTo.comment.forecast,
            comments: permTo.comment.forecast,
        },
        read: {
            comments: role.CESG,
            invoiceLink: permTo.view.invoice,
        },
    };
}
// ÄTA/FS
function ataFsPermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        create: {
            ata: permTo.edit.ata,
        },
        edit: {
            invoiced: permTo.edit.ata && role.CESG,
        },
    };
}
// ÄTA (Äta detaljer)
function ataPermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        edit: {
            answer: permTo.comment.ata,
            status: permTo.comment.ata,
            ata: permTo.edit.ata,
            description: permTo.edit.ata || (permTo.comment.ata && role.CESG),
        },
        save: permTo.edit.ata || permTo.comment.ata,
    };
}
// Fakturor
function invoicePermissions(projectId, user) {
    const permTo = createPermissions(projectId, user);
    const role = createRolePermissions(user);
    return {
        edit: {
            invoice: permTo.edit.invoice && role.CESG,
            project: permTo.edit.invoice && role.CESG,
            comments: permTo.comment.invoice && role.CESG,
            reviewers: permTo.edit.invoice && role.CESG,
            review: permTo.review,
        },
        read: {
            reviewers: role.CESG || (role.GUEST && permTo.edit.invoice),
            review: permTo.review,
            comments: role.CESG,
        },
    };
}
// Permissions can be seen in: https://docs.google.com/spreadsheets/d/1-64COCbTK7X6dxRjEmCXoich1t7jk75tsmhXK43JMVs/edit?gid=0#gid=0
export function pagePermissions(projectId, user) {
    if (!user) {
        return null;
    }
    return {
        overview: overviewPermissions(projectId, user),
        calculateBudget: calculateBudgetPermissions(projectId, user),
        forecast: forecastPermissions(projectId, user),
        ataFs: ataFsPermissions(projectId, user),
        ata: ataPermissions(projectId, user),
        invoices: invoicePermissions(projectId, user),
    };
}
